import { useState, useRef, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import {
  Search, PlusCircle, Lock, ChevronDown, X, ExternalLink, ChevronRight,
} from 'lucide-react'
import { useCatalogComponents } from '@/hooks/use-catalog'
import { CategoryFolderCard } from '@/components/gallery/category-folder-card'
import { usePages } from '@/hooks/use-pages'
import { useSubscription } from '@/hooks/use-subscription'
import { FeatureGate } from '@/components/feature-gate'
import { LivePreviewIframe } from '@/components/gallery/live-preview-iframe'
import { useAuthStore } from '@/stores/auth-store'
import type { CatalogComponent } from '@/types/components'

const PAGE_FEATURE_GATE: Record<string, string> = {
  members: 'members',
  events:  'events',
  music:   'music',
  shop:    'shop',
  cheki:   'cheki_form',
}

const DATA_LABEL: Record<string, string> = {
  members: 'Data Member',
  events: 'Data Event',
  cheki: 'Data Cheki',
  music: 'Data Musik',
  merch: 'Data Merch',
  member: 'Data Member (profil)',
}

const UPGRADE_TAB = '__upgrade__'

interface PageOption {
  label: string
  path: string
}

function AddToPageButton({
  componentKey,
  pageOptions,
}: {
  componentKey: string
  pageOptions: PageOption[]
}) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 })
  const btnRef = useRef<HTMLButtonElement>(null)
  const dropRef = useRef<HTMLDivElement>(null)

  function handleOpen() {
    if (!btnRef.current) return
    const rect = btnRef.current.getBoundingClientRect()
    setDropPos({ top: rect.bottom + 4, left: rect.left, width: rect.width })
    setOpen((prev) => !prev)
  }

  function handleSelect(path: string) {
    setOpen(false)
    navigate(path, { state: { addComponent: componentKey } })
  }

  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      const target = e.target as Node
      if (
        !btnRef.current?.contains(target) &&
        !dropRef.current?.contains(target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleOpen}
        className="mt-auto flex items-center justify-center gap-2 rounded-md border border-primary/50 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 transition-colors w-full"
      >
        <PlusCircle className="h-3.5 w-3.5 shrink-0" />
        <span className="flex-1 text-center">Tambah ke Halaman</span>
        <ChevronDown className="h-3 w-3 shrink-0" />
      </button>

      {open && createPortal(
        <div
          ref={dropRef}
          style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, minWidth: dropPos.width }}
          className="z-50 overflow-hidden rounded-lg border border-border bg-popover shadow-md"
        >
          {pageOptions.map((opt) => (
            <button
              key={opt.path}
              onClick={() => handleSelect(opt.path)}
              className="w-full px-3 py-2 text-left text-sm text-popover-foreground hover:bg-accent transition-colors"
            >
              {opt.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  )
}

function ComponentPreviewModal({
  component,
  pageOptions,
  onClose,
}: {
  component: CatalogComponent
  pageOptions: PageOption[]
  onClose: () => void
}) {
  const tenantSlug = useAuthStore((s) => s.currentTenant?.slug)
  const [loaded, setLoaded] = useState(false)

  const previewUrl = useMemo(() => {
    if (!tenantSlug) return null
    const publicBase =
      (import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined) ??
      `https://${tenantSlug}.oneidol.id`
    const block = {
      id: 'preview',
      component: component.component_key,
      enabled: true,
      order: 1,
      settings: component.default_settings,
    }
    return `${publicBase}/${tenantSlug}/preview?blocks=${encodeURIComponent(JSON.stringify([block]))}&demo=1`
  }, [tenantSlug, component.component_key, component.default_settings])

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      {/* Header */}
      <div className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-sm truncate">{component.label}</h2>
            <span className="shrink-0 text-[10px] uppercase tracking-wide bg-muted text-muted-foreground px-1.5 py-0.5 rounded font-medium">
              {component.category}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground truncate">{component.description}</p>
        </div>
        {previewUrl && (
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted transition-colors"
            title="Buka preview di tab baru"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
        <button
          onClick={onClose}
          className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Preview iframe */}
      <div className="relative flex-1 overflow-hidden bg-muted/20">
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          </div>
        )}
        {previewUrl ? (
          <iframe
            src={previewUrl}
            title={`Preview ${component.label}`}
            onLoad={() => setLoaded(true)}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              opacity: loaded ? 1 : 0,
              transition: 'opacity 0.2s ease',
            }}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            Preview tidak tersedia
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="shrink-0 border-t border-border px-4 py-3 space-y-2">
        {component.data_requirement && (
          <div className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
            <span className="text-xs text-muted-foreground">
              Membutuhkan {DATA_LABEL[component.data_requirement] ?? component.data_requirement}
              <span className="ml-1 opacity-60">(preview menggunakan data contoh)</span>
            </span>
          </div>
        )}
        <AddToPageButton
          componentKey={component.component_key}
          pageOptions={pageOptions}
        />
      </div>
    </div>,
    document.body
  )
}

export default function GalleryPage() {
  const [activeCategory, setActiveCategory] = useState<string>('all')
  const [search, setSearch] = useState('')
  const [previewComp, setPreviewComp] = useState<CatalogComponent | null>(null)

  const { data: allComponents = [], isLoading } = useCatalogComponents()
  const { data: pages = [] } = usePages()
  const { data: subscription, isSuccess: subLoaded } = useSubscription()
  const planFeatureKeys = new Set(
    (subscription?.plans?.plan_features ?? []).map((f) => f.feature_key)
  )

  const available = allComponents.filter((c) => !c.locked)
  const locked = allComponents.filter((c) => c.locked)

  const categories = [
    { id: 'all', label: 'Semua' },
    ...Array.from(new Set(available.map((c) => c.category))).map((cat) => ({
      id: cat,
      label: cat,
    })),
    ...(locked.length > 0 ? [{ id: UPGRADE_TAB, label: 'Upgrade Plan' }] : []),
  ]

  const isUpgradeTab = activeCategory === UPGRADE_TAB

  const filtered = isUpgradeTab
    ? locked.filter((c) => {
        if (!search.trim()) return true
        const q = search.toLowerCase()
        return c.label.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)
      })
    : available.filter((c) => {
        const matchCategory = activeCategory === 'all' || c.category === activeCategory
        if (!matchCategory) return false
        if (!search.trim()) return true
        const q = search.toLowerCase()
        return c.label.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)
      })

  // Build page options: Homepage first, then pages whose feature is in the plan.
  // System pages gate by PAGE_FEATURE_GATE; custom (non-system) pages gate by custom_pages.
  // While subscription is still loading (!subLoaded), show all pages gracefully.
  const pageOptions: PageOption[] = [
    { label: 'Homepage', path: '/pages' },
    ...pages
      .filter((p) => {
        if (!subLoaded) return true
        if (!p.is_system) return planFeatureKeys.has('custom_pages')
        const required = PAGE_FEATURE_GATE[p.slug]
        return !required || planFeatureKeys.has(required)
      })
      .map((p) => ({ label: p.title, path: `/pages/${p.slug}` })),
  ]

  // For member-detail scoped components, only show the member-profile page option.
  function getPageOptionsFor(def: CatalogComponent): PageOption[] {
    if (def.page_scope === 'member-detail') {
      const mp = pages.find((p) => p.slug === 'member-profile')
      return mp ? [{ label: mp.title, path: '/pages/member-profile' }] : []
    }
    return pageOptions
  }

  // Show category folder cards when on "Semua" tab with no active search
  const showFolderView = activeCategory === 'all' && !search.trim()

  if (isLoading) {
    return <p className="text-muted-foreground">Memuat komponen...</p>
  }

  return (
    <FeatureGate feature="gallery">
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Galeri Komponen</h1>
          <p className="text-muted-foreground">
            {available.length} komponen tersedia — pilih dan tambahkan ke halaman publik kamu.
          </p>
        </div>

        {/* Search */}
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari komponen..."
            className="w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`shrink-0 rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
              activeCategory === cat.id
                ? cat.id === UPGRADE_TAB
                  ? 'bg-amber-500 text-white'
                  : 'bg-primary text-primary-foreground'
                : cat.id === UPGRADE_TAB
                  ? 'border border-amber-300 text-amber-600 hover:border-amber-500'
                  : 'border border-border text-muted-foreground hover:text-foreground hover:border-primary/50'
            }`}
          >
            {cat.id === UPGRADE_TAB && <Lock className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />}
            {cat.label}
            <span className="ml-1.5 text-xs opacity-60">
              {cat.id === UPGRADE_TAB
                ? locked.length
                : available.filter((c) => cat.id === 'all' || c.category === cat.id).length}
            </span>
          </button>
        ))}
      </div>

      {/* Upgrade tab info banner */}
      {isUpgradeTab && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          Komponen di bawah ini membutuhkan tier yang lebih tinggi. Hubungi platform admin untuk upgrade paket langganan kamu.
        </div>
      )}

      {/* Folder view — "Semua" tab with no active search */}
      {showFolderView ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {categories
            .filter((cat) => cat.id !== 'all' && cat.id !== UPGRADE_TAB)
            .map((cat) => (
              <CategoryFolderCard
                key={cat.id}
                category={cat.id}
                components={available.filter((c) => c.category === cat.id)}
                onClick={() => setActiveCategory(cat.id)}
              />
            ))}
          {locked.length > 0 && (
            <button
              onClick={() => setActiveCategory(UPGRADE_TAB)}
              className="group w-full text-left rounded-xl border border-amber-200 bg-amber-50/40 hover:bg-amber-50 p-4 transition-all duration-150 hover:shadow-md"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="shrink-0 rounded-lg p-2 bg-amber-100 text-amber-600">
                  <Lock className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm text-amber-700">Upgrade Plan</h3>
                  <p className="text-xs text-amber-600/70">{locked.length} komponen terkunci</p>
                </div>
                <ChevronRight className="shrink-0 h-4 w-4 text-amber-500 group-hover:translate-x-0.5 transition-transform" />
              </div>
              <p className="text-xs text-amber-600/70">Upgrade tier untuk mengakses komponen premium.</p>
            </button>
          )}
        </div>
      ) : (
        /* Component grid — specific category tab or search active */
        <>
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-12 text-center">
              Tidak ada komponen yang cocok dengan pencarian.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {filtered.map((def) => (
                <div
                  key={def.component_key}
                  onClick={() => !isUpgradeTab && setPreviewComp(def)}
                  className={`rounded-xl border bg-card overflow-hidden flex flex-col relative transition-shadow ${
                    isUpgradeTab
                      ? 'border-border opacity-70'
                      : 'border-border cursor-pointer hover:shadow-md hover:border-primary/30'
                  }`}
                >
                  {/* Preview area */}
                  <LivePreviewIframe
                    componentKey={def.component_key}
                    defaultSettings={def.default_settings}
                    faded={isUpgradeTab}
                  />

                  {/* Info */}
                  <div className="p-4 flex-1 flex flex-col gap-3">
                    <div>
                      <div className="flex items-start gap-2">
                        <h3 className="font-semibold text-sm leading-tight flex-1">{def.label}</h3>
                        <span className="shrink-0 text-[10px] uppercase tracking-wide bg-muted text-muted-foreground px-1.5 py-0.5 rounded font-medium">
                          {def.category}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                        {def.description}
                      </p>
                    </div>

                    {def.data_requirement && (
                      <div className="flex items-center gap-1.5">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                        <span className="text-xs text-muted-foreground">
                          Membutuhkan {DATA_LABEL[def.data_requirement] ?? def.data_requirement}
                        </span>
                      </div>
                    )}

                    {isUpgradeTab ? (
                      <div className="mt-auto flex items-center justify-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-600">
                        <Lock className="h-3.5 w-3.5" />
                        Upgrade Plan
                      </div>
                    ) : (
                      // Stop propagation so clicking the button doesn't open the preview modal
                      <div onClick={(e) => e.stopPropagation()}>
                        <AddToPageButton
                          componentKey={def.component_key}
                          pageOptions={getPageOptionsFor(def)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>

    {/* Component preview modal */}
    {previewComp && (
      <ComponentPreviewModal
        component={previewComp}
        pageOptions={getPageOptionsFor(previewComp)}
        onClose={() => setPreviewComp(null)}
      />
    )}
    </FeatureGate>
  )
}
